import { demographicData, DemographicData } from '@/data/demographicData';

interface FormData {
  ageRange: [number, number];
  race: string[];
  politics: string;
  feminist: string;
  children: string;
  bodyType: string;
  familyStructure: string;
  maritalStatus: string;
  sexualPartnerCount: string;
  educationalLevel: string;
}

interface CalculationResult {
  probability: number;
  absoluteNumbers: {
    compatible: number;
    total: number;
  };
  breakdown: {
    factor: string;
    percentage: number;
  }[];
}

// Helper function to get age group from age range
function getAgeGroup(ageRange: [number, number]): string {
  const avgAge = (ageRange[0] + ageRange[1]) / 2;
  if (avgAge <= 24) return '18-24';
  if (avgAge <= 34) return '25-34';
  return '35-44';
}

// Helper function to calculate age range probability with multiple races
function calculateAgeRangeProbability(ageRange: [number, number], races: string[]): number {
  if (races.includes('all')) {
    return calculateAgeRangeProbabilityForRace(ageRange, 'all');
  }

  // Calculate weighted average for multiple races
  let totalProb = 0;
  let totalWeight = 0;
  
  races.forEach(race => {
    // Map new race categories to existing data
    const mappedRace = mapRaceToDataKey(race);
    const raceWeight = demographicData.population.by_race[mappedRace] || 0;
    if (raceWeight > 0) {
      const raceProb = calculateAgeRangeProbabilityForRace(ageRange, mappedRace);
      totalProb += raceProb * raceWeight;
      totalWeight += raceWeight;
    }
  });
  
  return totalWeight > 0 ? totalProb / totalWeight : 0;
}

// Helper function to map race categories to data keys
function mapRaceToDataKey(race: string): string {
  const raceMapping: Record<string, string> = {
    'all': 'all',
    'white': 'white',
    'black': 'black',
    'hispanic': 'hispanic',
    'asian': 'asian',
    'native-american': 'other',
    'middle-eastern': 'other',
    'pacific-islander': 'other',
    'multiracial': 'other',
    'other': 'other'
  };
  return raceMapping[race] || 'other';
}

// Helper function to calculate age range probability for a single race
function calculateAgeRangeProbabilityForRace(ageRange: [number, number], race: string): number {
  const mappedRace = mapRaceToDataKey(race);
  const ageData = demographicData.age_distribution[mappedRace];
  if (!ageData) return 0;

  // Calculate the proportion of women in the selected age range
  // using the age group distributions as weights
  let totalProportion = 0;
  
  // Define age group boundaries
  const ageGroups = [
    { key: '18-24', min: 18, max: 24, weight: ageData['18-24'] },
    { key: '25-34', min: 25, max: 34, weight: ageData['25-34'] },
    { key: '35-44', min: 35, max: 44, weight: ageData['35-44'] }
  ];
  
  // For each age group, calculate how much of it overlaps with the selected range
  ageGroups.forEach(group => {
    const overlapStart = Math.max(group.min, ageRange[0]);
    const overlapEnd = Math.min(group.max, ageRange[1]);
    
    if (overlapStart <= overlapEnd) {
      // Calculate what fraction of this age group is included
      const groupSpan = group.max - group.min + 1;
      const overlapSpan = overlapEnd - overlapStart + 1;
      const fractionIncluded = overlapSpan / groupSpan;
      
      // Add the weighted proportion
      totalProportion += group.weight * fractionIncluded;
    }
  });
  
  return Math.min(totalProportion, 1.0);
}

// Helper function to calculate weighted average by race
function weightedAverageByRace(races: string[], getValue: (race: string) => number): number {
  if (races.includes('all')) {
    return getValue('all');
  }
  
  let totalWeight = 0;
  let weightedSum = 0;
  
  races.forEach(race => {
    const mappedRace = mapRaceToDataKey(race);
    const raceWeight = demographicData.population.by_race[mappedRace] || 0;
    weightedSum += getValue(mappedRace) * raceWeight;
    totalWeight += raceWeight;
  });
  
  return totalWeight > 0 ? weightedSum / totalWeight : 0;
}

// Helper function to calculate age weights for sexual partner count
function calculateAgeWeights(ageRange: [number, number]): Record<string, number> {
  const ageGroups = ['18-24', '25-34', '35-44'];
  const weights: Record<string, number> = {};
  
  ageGroups.forEach(group => {
    const [minAge, maxAge] = group.split('-').map(Number);
    const overlap = Math.max(0, Math.min(ageRange[1], maxAge) - Math.max(ageRange[0], minAge) + 1);
    const totalRange = ageRange[1] - ageRange[0] + 1;
    weights[group] = overlap / totalRange;
  });
  
  return weights;
}

export function calculateCompatibility(formData: FormData): CalculationResult {
  const breakdown: { factor: string; percentage: number }[] = [];
  let totalProbability = 1.0;

  // Base population by race(s)
  let raceProb = 1.0;
  if (!formData.race.includes('all')) {
    // Sum probabilities for selected races using mapped keys
    raceProb = formData.race.reduce((sum, race) => {
      const mappedRace = mapRaceToDataKey(race);
      return sum + (demographicData.population.by_race[mappedRace] || 0);
    }, 0);
    
    const raceLabels = formData.race.map(race => {
      const labels: Record<string, string> = {
        white: 'White (Non-Hispanic)',
        black: 'Black or African American',
        hispanic: 'Hispanic or Latino',
        asian: 'Asian',
        'native-american': 'Native American or Alaska Native',
        'middle-eastern': 'Middle Eastern or North African',
        'pacific-islander': 'Pacific Islander',
        multiracial: 'Multiracial/Mixed Race',
        other: 'Other'
      };
      return labels[race] || race;
    });
    
    breakdown.push({
      factor: `Race/Ethnicity: ${raceLabels.join(', ')}`,
      percentage: raceProb * 100
    });
  }
  totalProbability *= raceProb;

  // Age range probability
  const ageProb = calculateAgeRangeProbability(formData.ageRange, formData.race);
  breakdown.push({
    factor: `Age Range: ${formData.ageRange[0]}-${formData.ageRange[1]}`,
    percentage: ageProb * 100
  });
  totalProbability *= ageProb;

  // Political affiliation - use weighted average for multiple races
  if (formData.politics !== 'no-preference') {
    let politicsProb = 0;
    
    if (formData.race.includes('all')) {
      const politicsData = demographicData.political_affiliation.all;
      politicsProb = politicsData[formData.politics] || 0.33;
    } else {
      let totalWeight = 0;
      formData.race.forEach(race => {
        const mappedRace = mapRaceToDataKey(race);
        const politicsData = demographicData.political_affiliation[mappedRace];
        const raceWeight = demographicData.population.by_race[mappedRace] || 0;
        if (politicsData) {
          politicsProb += (politicsData[formData.politics] || 0.33) * raceWeight;
          totalWeight += raceWeight;
        }
      });
      politicsProb = totalWeight > 0 ? politicsProb / totalWeight : 0.33;
    }
    
    breakdown.push({
      factor: `Political Leaning: ${formData.politics}`,
      percentage: politicsProb * 100
    });
    totalProbability *= politicsProb;
  }

  // Feminist identification - use weighted average for multiple races
  if (formData.feminist !== 'no-preference') {
    let feministProb = 0;
    
    if (formData.race.includes('all')) {
      const feministRate = demographicData.feminist_identification.all;
      feministProb = formData.feminist === 'no' ? (1 - feministRate) : feministRate;
    } else {
      let totalWeight = 0;
      formData.race.forEach(race => {
        const mappedRace = mapRaceToDataKey(race);
        const feministRate = demographicData.feminist_identification[mappedRace];
        const raceWeight = demographicData.population.by_race[mappedRace] || 0;
        if (feministRate !== undefined) {
          const raceFeministProb = formData.feminist === 'no' ? (1 - feministRate) : feministRate;
          feministProb += raceFeministProb * raceWeight;
          totalWeight += raceWeight;
        }
      });
      feministProb = totalWeight > 0 ? feministProb / totalWeight : 0.28;
    }
    
    breakdown.push({
      factor: `Feminist Identification: ${formData.feminist === 'no' ? 'Non-feminist' : 'Feminist'}`,
      percentage: feministProb * 100
    });
    totalProbability *= feministProb;
  }

  // Parental status - use weighted average for multiple races
  if (formData.children !== 'no-preference') {
    const ageGroup = getAgeGroup(formData.ageRange);
    let noChildrenProb = 0;
    
    if (formData.race.includes('all')) {
      const childrenData = demographicData.parental_status.all;
      noChildrenProb = childrenData[ageGroup] || 0.5;
    } else {
      let totalWeight = 0;
      formData.race.forEach(race => {
        const mappedRace = mapRaceToDataKey(race);
        const childrenData = demographicData.parental_status[mappedRace];
        const raceWeight = demographicData.population.by_race[mappedRace] || 0;
        if (childrenData) {
          noChildrenProb += (childrenData[ageGroup] || 0.5) * raceWeight;
          totalWeight += raceWeight;
        }
      });
      noChildrenProb = totalWeight > 0 ? noChildrenProb / totalWeight : 0.5;
    }
    
    breakdown.push({
      factor: 'No Children',
      percentage: noChildrenProb * 100
    });
    totalProbability *= noChildrenProb;
  }

  // Body type (BMI) - use weighted average for multiple races
  if (formData.bodyType !== 'no-preference') {
    let normalWeightProb = 0;
    
    if (formData.race.includes('all')) {
      const bmiData = demographicData.bmi_distribution.all;
      normalWeightProb = bmiData.normal_weight || 0.35;
    } else {
      let totalWeight = 0;
      formData.race.forEach(race => {
        const mappedRace = mapRaceToDataKey(race);
        const bmiData = demographicData.bmi_distribution[mappedRace];
        const raceWeight = demographicData.population.by_race[mappedRace] || 0;
        if (bmiData) {
          normalWeightProb += (bmiData.normal_weight || 0.35) * raceWeight;
          totalWeight += raceWeight;
        }
      });
      normalWeightProb = totalWeight > 0 ? normalWeightProb / totalWeight : 0.35;
    }
    
    breakdown.push({
      factor: 'Not Overweight (BMI < 25)',
      percentage: normalWeightProb * 100
    });
    totalProbability *= normalWeightProb;
  }

  // Family structure - use weighted average for multiple races
  if (formData.familyStructure !== 'no-preference') {
    let twoParentsProb = 0;
    
    if (formData.race.includes('all')) {
      twoParentsProb = demographicData.family_structure.all;
    } else {
      let totalWeight = 0;
      formData.race.forEach(race => {
        const mappedRace = mapRaceToDataKey(race);
        const familyProb = demographicData.family_structure[mappedRace];
        const raceWeight = demographicData.population.by_race[mappedRace] || 0;
        if (familyProb !== undefined) {
          twoParentsProb += familyProb * raceWeight;
          totalWeight += raceWeight;
        }
      });
      twoParentsProb = totalWeight > 0 ? twoParentsProb / totalWeight : 0.65;
    }
    
    breakdown.push({
      factor: 'Raised by Two Parents',
      percentage: twoParentsProb * 100
    });
    totalProbability *= twoParentsProb;
  }

  // Marital status - use weighted average for multiple races
  if (formData.maritalStatus !== 'no-preference') {
    const ageGroup = getAgeGroup(formData.ageRange);
    let maritalProb = 0;
    
    if (formData.race.includes('all')) {
      const maritalData = demographicData.marital_status.all;
      if (formData.maritalStatus === 'never-married') {
        maritalProb = maritalData.never_married[ageGroup] || 0.5;
      } else if (formData.maritalStatus === 'divorced-separated') {
        maritalProb = maritalData.divorced_separated[ageGroup] || 0.15;
      }
    } else {
      let totalWeight = 0;
      formData.race.forEach(race => {
        const mappedRace = mapRaceToDataKey(race);
        const maritalData = demographicData.marital_status[mappedRace];
        const raceWeight = demographicData.population.by_race[mappedRace] || 0;
        if (maritalData) {
          let raceMaritalProb = 0;
          if (formData.maritalStatus === 'never-married') {
            raceMaritalProb = maritalData.never_married[ageGroup] || 0.5;
          } else if (formData.maritalStatus === 'divorced-separated') {
            raceMaritalProb = maritalData.divorced_separated[ageGroup] || 0.15;
          }
          maritalProb += raceMaritalProb * raceWeight;
          totalWeight += raceWeight;
        }
      });
      maritalProb = totalWeight > 0 ? maritalProb / totalWeight : 0.5;
    }
    
    const statusLabel = formData.maritalStatus === 'never-married' ? 'Never Married' : 'Divorced/Separated';
    breakdown.push({
      factor: `Marital Status: ${statusLabel}`,
      percentage: maritalProb * 100
    });
    totalProbability *= maritalProb;
  }

  // Sexual partner count - use age and race-weighted calculation
  let sexualPartnerProb = 1;
  if (formData.sexualPartnerCount !== 'no-preference') {
    // Age-weighted calculation for sexual partner count
    const ageWeights = calculateAgeWeights(formData.ageRange);
    let ageWeightedProb = 0;
    
    Object.entries(ageWeights).forEach(([ageGroup, weight]) => {
      const ageGroupData = demographicData.sexual_partner_count.by_age[ageGroup];
      if (ageGroupData && weight > 0) {
        ageWeightedProb += (ageGroupData[formData.sexualPartnerCount] || 0) * weight;
      }
    });

    // Race-weighted calculation if races are specified
    if (!formData.race.includes('all')) {
      const raceWeightedProb = weightedAverageByRace(
        formData.race,
        (race) => {
          const mappedRace = mapRaceToDataKey(race);
          const raceData = demographicData.sexual_partner_count.by_race[mappedRace];
          return raceData ? (raceData[formData.sexualPartnerCount] || 0) : (demographicData.sexual_partner_count.all[formData.sexualPartnerCount] || 0);
        }
      );
      // Combine age and race weighting (average of both)
      sexualPartnerProb = (ageWeightedProb + raceWeightedProb) / 2;
    } else {
      sexualPartnerProb = ageWeightedProb;
    }
    
    const experienceLabels: Record<string, string> = {
      zero: 'Virgin/Pure (0 partners)',
      low: 'Low Experience (1-3 partners)',
      moderate: 'Moderate Experience (4-9 partners)',
      high: 'High Experience (10+ partners)'
    };
    
    breakdown.push({
      factor: `Sexual Experience: ${experienceLabels[formData.sexualPartnerCount] || formData.sexualPartnerCount}`,
      percentage: sexualPartnerProb * 100
    });
    totalProbability *= sexualPartnerProb;
  }

  // Educational level - use age and race-weighted calculation
  if (formData.educationalLevel !== 'no-preference') {
    const ageGroup = getAgeGroup(formData.ageRange);
    let educationProb = 0;
    
    if (formData.race.includes('all')) {
      const educationData = demographicData.educational_attainment.all;
      const levelData = educationData[formData.educationalLevel as keyof typeof educationData];
      educationProb = levelData ? (levelData[ageGroup] || 0.25) : 0.25;
    } else {
      let totalWeight = 0;
      formData.race.forEach(race => {
        const mappedRace = mapRaceToDataKey(race);
        const educationData = demographicData.educational_attainment[mappedRace];
        const raceWeight = demographicData.population.by_race[mappedRace] || 0;
        if (educationData) {
          const levelData = educationData[formData.educationalLevel as keyof typeof educationData];
          const raceEducationProb = levelData ? (levelData[ageGroup] || 0.25) : 0.25;
          educationProb += raceEducationProb * raceWeight;
          totalWeight += raceWeight;
        }
      });
      educationProb = totalWeight > 0 ? educationProb / totalWeight : 0.25;
    }
    
    const educationLabels: Record<string, string> = {
      'high_school_or_less': 'High School or Less',
      'some_college': 'Some College',
      'bachelors_degree': "Bachelor's Degree",
      'graduate_degree': 'Graduate Degree'
    };
    
    breakdown.push({
      factor: `Education: ${educationLabels[formData.educationalLevel] || formData.educationalLevel}`,
      percentage: educationProb * 100
    });
    totalProbability *= educationProb;
  }

  // Calculate absolute numbers
  const basePopulation = demographicData.population.total_women_18_44;
  const compatibleWomen = Math.round(basePopulation * totalProbability);

  return {
    probability: totalProbability * 100,
    absoluteNumbers: {
      compatible: compatibleWomen,
      total: basePopulation
    },
    breakdown
  };
}